import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_THEME, LIMITS, UNAVAILABLE_DISPLAY, computeReportViews, dataAsOf, resolvePrintOrder, themeFontStacks, visibleRowIndices } from '@tallyframe/core';
import { WidgetBody } from './internal/WidgetBody.js';
import { PrintCover } from './internal/PrintCover.js';
import { BrandContext, brandStyle, resolveBrand } from './internal/brand.js';
import { BOX_SHARES, pageBandsCss } from './internal/print-bands.js';
import { fallbackMeasure, fitText } from './internal/fit-text.js';
import { printContentWidthInches } from './internal/print-geometry.js';
import { fontLoadShorthands, themeStyle } from './internal/theme-style.js';
/**
 * The paged document.
 *
 * This is deliberately *not* the dashboard grid: absolute grid coordinates do
 * not survive pagination. The report flows — cover, then the key figures, then
 * charts at a fixed printable size, then the authored notes, then the detail
 * table last so it can spill across as many pages as it needs with its header
 * row repeated on each one.
 *
 * Order comes from core's `resolvePrintOrder`, so a widget added after the print
 * order was saved still prints rather than silently disappearing.
 */
/** Printable content width at 96dpi. The horizontal `@page` margin is mirrored
 *  in one place only, `printContentWidthInches` (internal/print-geometry.ts),
 *  which print.css's `@page` rule points back to. Charts get an explicit pixel
 *  box because `responsive: false` means Chart.js will not consult the layout. */
function printChartSize(print) {
    const width = Math.floor(printContentWidthInches(print) * 96);
    return { width, height: Math.round(width * 0.42) };
}
/** Measures a string in points at the bands' 8.5pt, in the theme's body font.
 *  Called only after the font loads settle, so the canvas measures the loaded
 *  face. Without a 2D context, `fallbackMeasure` over-cuts rather than risk a
 *  wrapped margin box. */
function makeMeasure(theme) {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx)
        return fallbackMeasure;
    ctx.font = `8.5pt ${themeFontStacks(theme).body}`;
    return (s) => ctx.measureText(s).width * 0.75; // px -> pt
}
function byType(widgets, type) {
    return widgets.filter((widget) => widget.type === type);
}
/**
 * Splits the print order into runs of consecutive same-type widgets.
 *
 * The document follows `print.order` exactly. Grouping only *adjacent* widgets
 * keeps three KPIs in a row sharing one grid of key figures, without ever
 * moving a widget out of its configured position. An earlier version bucketed
 * every widget by type into fixed sections, so a note configured to follow a
 * table was printed before it regardless.
 *
 * Tables never merge: each flows across its own run of pages.
 */
function toRuns(widgets) {
    const runs = [];
    for (const widget of widgets) {
        const last = runs.at(-1);
        if (last && last.type === widget.type && widget.type !== 'table') {
            last.widgets.push(widget);
        }
        else {
            runs.push({ type: widget.type, widgets: [widget] });
        }
    }
    return runs;
}
/** Assigns a print-order sequence number ("01", "02", …) to every chart,
 *  table and note, skipping KPIs — spec §8.2 numbers "the chart, table and
 *  note titles", never the key figures. */
function numberBlocks(widgets) {
    const numbers = new Map();
    let next = 0;
    for (const widget of widgets) {
        if (widget.type === 'kpi')
            continue;
        next += 1;
        numbers.set(widget.id, String(next).padStart(2, '0'));
    }
    return numbers;
}
function groupSafeCut(view, visible, cap) {
    if (visible.length <= cap)
        return { printed: visible.length, atBoundary: true };
    if (!view.nodes)
        return { printed: cap, atBoundary: false };
    for (let end = cap; end > 0; end--) {
        const node = view.nodes[visible[end]];
        if (node.depth === 0 && node.kind === 'group')
            return { printed: end, atBoundary: true };
    }
    // No boundary found within the cap at all — the first (or only) top-level
    // group's own subtree already exceeds it. Index 0 itself is deliberately
    // excluded from the search above (`end > 0`): cutting there would print an
    // empty table, and a partial group beats that.
    return { printed: cap, atBoundary: false };
}
function PrintTable({ view }) {
    // A printed report is not a data dump. Past LIMITS.maxPrintTableRows the table
    // stops being readable and, for large uploads, stops rendering at all inside
    // the export budget. Only the table is capped: `view` was computed from every
    // row, so KPIs and charts elsewhere on the page are still exact.
    //
    // Print renders at the definition's `defaultExpandedDepth` only — there is no
    // interaction to collapse against, so `maxDepth` is exactly the right ceiling
    // (unlike the screen, which had to drop it: see WidgetBody's GroupedTableBody).
    // Group rows count against the cap like any other row, since Chromium lays
    // each one out the same way; truncation snaps backward to a top-level group
    // boundary so a subtotal never prints with its children cut off.
    const visible = visibleRowIndices(view, { maxDepth: view.widget.defaultExpandedDepth });
    const cut = groupSafeCut(view, visible, LIMITS.maxPrintTableRows);
    const printed = cut.printed;
    const truncated = printed < visible.length;
    const shown = visible.slice(0, printed);
    return (_jsxs(_Fragment, { children: [_jsxs("table", { className: "tf-print-table", children: [_jsx("caption", { children: view.widget.title }), _jsx("thead", { children: _jsx("tr", { children: view.columns.map((column) => (_jsx("th", { scope: "col", className: column.numeric ? 'tf-cell-num' : undefined, children: column.label }, column.columnId))) }) }), _jsx("tbody", { children: shown.map((rowIndex) => {
                            const node = view.nodes?.[rowIndex];
                            const row = view.formatRows(rowIndex, rowIndex + 1)[0];
                            return (_jsx("tr", { className: node?.kind === 'group' ? 'tf-tree-group' : undefined, children: view.columns.map((column, columnIndex) => (_jsx("td", { className: column.numeric ? 'tf-cell-num' : undefined, style: columnIndex === 0 && node ? { paddingLeft: `${node.depth * 12}px` } : undefined, children: row[columnIndex] ?? UNAVAILABLE_DISPLAY }, column.columnId))) }, rowIndex));
                        }) })] }), truncated ? (
            // Stated on the page itself, with the count the reader can actually
            // reach: with grouping, `visible.length` (what's reachable at this
            // depth) and `view.totalRows` (every group and leaf, at every depth)
            // differ, and quoting the total would be the misleading-report failure
            // this note exists to avoid.
            _jsxs("p", { className: "tf-print-truncated", "data-testid": "print-table-truncated", children: ["Showing the first ", printed.toLocaleString('en-US'), " of ", visible.length.toLocaleString('en-US'), " rows", view.groupDepth > 0
                        ? cut.atBoundary
                            ? ', cut at a group boundary'
                            : ', cut mid-group because the first group exceeds the limit'
                        : '', view.widget.sort ? ', in the table’s sort order' : '', "."] })) : null] }));
}
export function PrintReport(props) {
    const { definition, data } = props;
    const theme = props.brand?.theme ?? DEFAULT_THEME;
    const locale = props.locale ?? theme.locale ?? 'en-US';
    const badge = props.badge ?? true;
    const views = useMemo(() => computeReportViews(definition, data, locale), [definition, data, locale]);
    const ordered = useMemo(() => resolvePrintOrder(definition), [definition]);
    const resolvedBrand = useMemo(() => resolveBrand(props.brand), [props.brand]);
    // Charts are counted separately only because readiness waits on each of them;
    // the document itself is laid out from `runs`, in print order.
    const charts = byType(ordered, 'chart');
    const runs = useMemo(() => toRuns(ordered), [ordered]);
    const blockNumbers = useMemo(() => (theme.blockNumbers ? numberBlocks(ordered) : new Map()), [ordered, theme.blockNumbers]);
    const chartSize = printChartSize(definition.print);
    // --- page bands ----------------------------------------------------------
    // Margin boxes cannot wrap or clip, so each string is cut to its box by
    // measured width. Measuring needs the theme's body font loaded, so fitting
    // waits on `document.fonts.load` for every theme shorthand; a rejected load
    // still fits rather than wedging the export. ChartCanvas runs its own
    // per-chart font wait; this one serves the furniture and the ready gate.
    const [bands, setBands] = useState(null);
    useEffect(() => {
        let live = true;
        const fonts = document.fonts;
        const loaded = fonts && typeof fonts.load === 'function'
            ? Promise.all(fontLoadShorthands(theme).map((shorthand) => fonts.load(shorthand)))
            : Promise.resolve();
        const fit = () => {
            if (!live)
                return;
            const measure = makeMeasure(theme);
            const across = printContentWidthInches(definition.print) * 72; // pt
            setBands({
                title: fitText(definition.title, across * BOX_SHARES.topLeft, measure),
                operator: fitText(resolvedBrand.name, across * BOX_SHARES.topRight, measure),
                footerText: fitText(theme.footerText, across * BOX_SHARES.bottomLeft, measure)
            });
        };
        void loaded.then(fit, fit);
        return () => {
            live = false;
        };
    }, [theme, definition.title, resolvedBrand.name, definition.print]);
    // --- readiness -----------------------------------------------------------
    const totalCharts = charts.length;
    const renderedRef = useRef(new Set());
    const [renderedCount, setRenderedCount] = useState(0);
    const firedRef = useRef(false);
    const onReadyRef = useRef(props.onReady);
    onReadyRef.current = props.onReady;
    const handleChartRendered = useCallback((widgetId) => {
        if (renderedRef.current.has(widgetId))
            return;
        renderedRef.current.add(widgetId);
        setRenderedCount(renderedRef.current.size);
    }, []);
    useEffect(() => {
        if (firedRef.current)
            return;
        if (renderedCount < totalCharts)
            return;
        // Bands are fitted only after the theme font loads settle, so this one
        // check gates on both.
        if (bands === null)
            return;
        let cancelled = false;
        const fonts = document.fonts;
        const fontsReady = fonts?.ready ?? Promise.resolve(undefined);
        void fontsReady.then(() => {
            if (cancelled || firedRef.current)
                return;
            firedRef.current = true;
            // Exact order — Playwright and the export pipeline depend on it.
            document.body.dataset.printReady = 'true';
            window.__TALLYFRAME_PRINT_READY__ = true;
            onReadyRef.current?.();
        }, () => {
            // A font that never loads must not wedge an export forever.
            if (cancelled || firedRef.current)
                return;
            firedRef.current = true;
            document.body.dataset.printReady = 'true';
            window.__TALLYFRAME_PRINT_READY__ = true;
            onReadyRef.current?.();
        });
        return () => {
            cancelled = true;
        };
    }, [renderedCount, totalCharts, bands]);
    // --- provenance ----------------------------------------------------------
    const sourceClaim = data.snapshot.sourceDescription ?? data.dataset.sourceDescription;
    const className = ['tf-print', props.className].filter(Boolean).join(' ');
    // No bands <style> until the strings are fitted: unfitted ones could wrap a
    // margin box, and print-ready waits for the fitted ones anyway.
    const bandsCss = bands
        ? pageBandsCss({
            title: bands.title,
            operator: bands.operator,
            brandMark: resolvedBrand.mark,
            badge,
            footerText: bands.footerText,
            fontStack: themeFontStacks(theme).body,
            print: definition.print
        })
        : null;
    // Only a host that passes a theme gets inline font stacks; without one,
    // tokens.css's defaults stand exactly as before theming.
    const rootStyle = props.brand?.theme
        ? { ...brandStyle(resolvedBrand), ...themeStyle(props.brand.theme) }
        : brandStyle(resolvedBrand);
    const closingNote = theme.closingNote.trim() ? theme.closingNote : '';
    return (_jsx(BrandContext.Provider, { value: resolvedBrand, children: _jsxs("article", { className: className, style: rootStyle, children: [bandsCss ? _jsx("style", { children: bandsCss }) : null, _jsx(PrintCover, { definition: definition, brand: resolvedBrand, theme: theme, dataAsOf: dataAsOf(data.snapshot), issuedAt: props.issuedAt, locale: locale }), runs.map((run) => {
                    const key = run.widgets.map((w) => w.id).join('|');
                    if (run.type === 'table') {
                        const widget = run.widgets[0];
                        const view = views.get(widget.id);
                        if (!view || view.kind !== 'table')
                            return null;
                        return (_jsxs("section", { className: "tf-print-table-block", children: [blockNumbers.has(widget.id) ? (_jsx("span", { className: "tf-print-block-number", children: blockNumbers.get(widget.id) })) : null, _jsx(PrintTable, { view: view })] }, key));
                    }
                    if (run.type === 'kpi') {
                        return (_jsx("section", { className: "tf-print-section", children: _jsx("div", { className: "tf-print-kpis", children: run.widgets.map((widget) => {
                                    const view = views.get(widget.id);
                                    if (!view)
                                        return null;
                                    return (_jsx("div", { className: "tf-print-kpi", children: _jsx(WidgetBody, { view: view, animate: false, paginate: false }) }, widget.id));
                                }) }) }, key));
                    }
                    return (_jsx("section", { className: "tf-print-section", children: run.widgets.map((widget) => {
                            const view = views.get(widget.id);
                            if (!view)
                                return null;
                            return (_jsxs("div", { className: "tf-print-block", children: [blockNumbers.has(widget.id) ? (_jsx("span", { className: "tf-print-block-number", children: blockNumbers.get(widget.id) })) : null, _jsx("h2", { className: "tf-print-block-title", children: widget.title }), run.type === 'chart' ? (_jsx(WidgetBody, { view: view, animate: false, paginate: false, chartWidth: chartSize.width, chartHeight: chartSize.height, onChartRendered: () => handleChartRendered(widget.id) })) : (_jsx(WidgetBody, { view: view, animate: false, paginate: false }))] }, widget.id));
                        }) }, key));
                }), definition.print.includeSourceNotes && sourceClaim ? (_jsxs("p", { className: "tf-print-source", children: ["Source: ", sourceClaim] })) : null, closingNote ? _jsx("p", { className: "tf-print-closing", children: closingNote }) : null] }) }));
}
//# sourceMappingURL=PrintReport.js.map